import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type {
  AvailableMethod,
  FreeShippingPayload,
  Market,
  MarketPayload,
  OohUpgradePayload,
  PromiseDatePayload,
} from '../types/services';

type Wrapped<T> = { data: T };

export function useMarkets() {
  const api = useApiClient();
  return useQuery<Market[], Error>({
    queryKey: queryKeys.markets.list(),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<Market[]>>('/admin/markets', signal);
      return response.data;
    },
  });
}

/**
 * Catalog methods offered within a market (configured service nested when present).
 * Lazy by design: pass `enabled: false` until the market section is expanded.
 */
export function useMarketAvailableMethods(marketId: number, options?: { enabled?: boolean }) {
  const api = useApiClient();
  return useQuery<AvailableMethod[], Error>({
    queryKey: queryKeys.markets.availableMethods(marketId),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<AvailableMethod[]>>(
        `/admin/markets/${marketId}/available-methods`,
        signal,
      );
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useCreateMarket() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Market, Error, MarketPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<Market>>('/admin/markets', payload);
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
    },
  });
}

export function useUpdateMarket(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Market, Error, Partial<MarketPayload>>({
    mutationFn: async (payload) => {
      const response = await api.patch<Wrapped<Market>>(`/admin/markets/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      // Country changes shift each method's available/excluded sets too.
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
    },
  });
}

/** Save a market's cut-off / promise-date widget configuration. */
export function useUpdatePromiseDate(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Market, Error, PromiseDatePayload>({
    mutationFn: async (payload) => {
      const response = await api.patch<Wrapped<Market>>(
        `/admin/markets/${id}/promise-date`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
    },
  });
}

/** Toggle a market's free-shipping countdown widget. */
export function useUpdateFreeShipping(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Market, Error, FreeShippingPayload>({
    mutationFn: async (payload) => {
      const response = await api.patch<Wrapped<Market>>(
        `/admin/markets/${id}/free-shipping`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
    },
  });
}

/** Toggle a market's checkout out-of-home (OOH) upgrade pop-up. */
export function useUpdateOohUpgrade(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Market, Error, OohUpgradePayload>({
    mutationFn: async (payload) => {
      const response = await api.patch<Wrapped<Market>>(
        `/admin/markets/${id}/ooh-upgrade`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
    },
  });
}

export function useDeleteMarket() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.del(`/admin/markets/${id}`),
    onSuccess: () => {
      // Deleting a market also deletes its configured services.
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
      void qc.invalidateQueries({ queryKey: queryKeys.carrierServices.all });
    },
  });
}
